import { Component, OnInit, viewChild } from '@angular/core'
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { customEmailValidator, validateAllFormFields } from 'models/form'
import { IntegrationAuthEvent } from 'models/integration-event'
import { OAuth } from 'models/oauth'
import { BaseModule } from 'shared/base/base.module'
import {
  LoginAction,
  ServerError,
  SetSessionTokenAction,
} from 'src/app/actions/auth'
import { State } from 'src/app/reducers'
import { IntegrationsService } from 'src/app/services/integrations.service'
import {
  isProdBetaEnvironment,
  isProdEnvironment,
} from 'utils/environment-utils'
import { CustomError } from '../../../constants/errors'
import { AuthService } from '../../../services/hubtype-api/auth.service'
import { LoginEnsModalComponent } from '../login-ens-modal/login-ens-modal.component'

@Component({
  selector: 'login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    RouterModule,
    BaseModule,
    TranslatePipe,
    LoginEnsModalComponent,
  ],
})
export class LoginFormComponent implements OnInit {
  readonly ensModal = viewChild<LoginEnsModalComponent>('ensModal')

  error: string
  isSigningIn = false
  form = this.fb.group({
    email: ['', [Validators.required, customEmailValidator()]],
    password: ['', [Validators.required]],
  })

  private successLoginResponse: OAuth

  constructor(
    private store: Store<State>,
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private integrationsService: IntegrationsService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      this.error = null
    })
  }

  onSubmit() {
    validateAllFormFields(this.form)
    if (this.form.invalid) {
      return
    }
    const { email, password } = this.form.value
    this.isSigningIn = true

    const returnUrl = this.route.snapshot.queryParams['returnUrl']

    this.authService.login(email, password).subscribe({
      next: res => {
        if (this.shouldShowEnsModal()) {
          this.successLoginResponse = res
          this.ensModal().open()
          return
        }

        this.store.dispatch(new LoginAction(res))

        if (this.integrationsService.isUsingIntegrations()) {
          console.log('Integration: send authenticated event')
          this.integrationsService.sendEvent(new IntegrationAuthEvent(res))
        }
      },
      error: ({ error }) => {
        if (
          error.status === 401 &&
          error.data?.flows?.find(flow => flow.id === 'mfa_authenticate')
        ) {
          this.store.dispatch(new ServerError('MFA_AUTHENTICATE'))
          this.store.dispatch(
            new SetSessionTokenAction(error.meta.session_token)
          )
          this.router.navigate(['/login-2fa'], {
            queryParams: { returnUrl },
          })
        } else {
          this.isSigningIn = false
          this.error =
            error?.errors?.[0]?.message ||
            this.translate.instant(CustomError.LOGIN_ERROR.messageKey)
        }
      },
    })
  }

  onEnsModalClosed() {
    if (!this.successLoginResponse) {
      return
    }

    this.store.dispatch(new LoginAction(this.successLoginResponse))
  }

  private shouldShowEnsModal() {
    const { email } = this.form.value
    const isHubtypeEmail = email.includes('@hubtype.com')
    const isProd = isProdEnvironment()
    const isBeta = isProdBetaEnvironment()

    return isHubtypeEmail && (isProd || isBeta)
  }
}
